import { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faExclamationCircle, faInbox, faEye, faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { authFetch, formatDate, formatDateTime, openAuthedFile, DOCUMENT_TYPE_LABELS, DOCUMENT_DISPLAY_ORDER } from '../../utils/http';

interface AppDoc {
  id: string;
  documentType: number;
  fileName: string;
  fileSizeBytes: number;
}

interface Application {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  idNumber: string | null;
  dateOfBirth: string | null;
  address: string;
  programme: string;
  motivation: string | null;
  status: number;
  reviewNote: string | null;
  submittedAt: string;
  reviewedAt: string | null;
  documents: AppDoc[];
}

const STATUS = [
  { label: 'Pending', badge: 'bg-amber-100 text-amber-700' },
  { label: 'Accepted', badge: 'bg-green-100 text-green-700' },
  { label: 'Rejected', badge: 'bg-red-100 text-red-700' },
];

const FILTERS = [
  { id: 0, label: 'Pending' },
  { id: 1, label: 'Accepted' },
  { id: 2, label: 'Rejected' },
];

export default function AdminApplicationsPage() {
  const { accessToken } = useAuth();
  const [filter, setFilter] = useState(0);
  const [items, setItems] = useState<Application[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejectNotes, setRejectNotes] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setIsLoading(true);
    const res = await authFetch<Application[]>(accessToken, `/api/applications?status=${filter}`);
    if (res.ok && Array.isArray(res.data)) {
      setItems(res.data);
      setError('');
    } else {
      setError(res.message || 'Failed to load applications.');
    }
    setIsLoading(false);
  }, [accessToken, filter]);

  useEffect(() => {
    load();
  }, [load]);

  const view = async (docId: string) => {
    const err = await openAuthedFile(accessToken, `/api/applications/documents/${docId}/file`);
    if (err) setError(err);
  };

  const accept = async (a: Application) => {
    if (!window.confirm(`Accept ${a.firstName} ${a.lastName}? This creates their student account and saves their documents.`)) return;
    setError('');
    setNotice('');
    setBusyId(a.id);
    const res = await authFetch(accessToken, `/api/applications/${a.id}/accept`, { method: 'POST' });
    setBusyId(null);
    if (!res.ok) return setError(res.message);
    setNotice(res.message);
    setOpenId(null);
    load();
  };

  const reject = async (a: Application) => {
    const note = (rejectNotes[a.id] ?? '').trim();
    if (!note) return setError('Add a short reason before rejecting.');
    if (!window.confirm(`Reject ${a.firstName} ${a.lastName}? Their uploaded documents will be deleted.`)) return;
    setError('');
    setNotice('');
    setBusyId(a.id);
    const res = await authFetch(accessToken, `/api/applications/${a.id}/reject`, { method: 'POST', body: { note } });
    setBusyId(null);
    if (!res.ok) return setError(res.message);
    setNotice(res.message);
    setOpenId(null);
    load();
  };

  const row = (label: string, value: string | null | undefined) => (
    <div>
      <p className="text-xs font-bold uppercase tracking-wider text-brand-900/40">{label}</p>
      <p className="text-sm text-brand-950 break-words">{value || '—'}</p>
    </div>
  );

  return (
    <>
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10">
        <h1 className="text-2xl font-bold text-brand-950">Applications</h1>
        <p className="text-sm text-brand-900/60 mt-1">Review online applications, then accept or reject them</p>
      </div>

      <div className="p-8 space-y-5">
        <div className="flex gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                filter === f.id ? 'bg-brand-700 text-white' : 'border border-canvas-300 text-brand-900/70 hover:bg-canvas-100'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {notice && <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">{notice}</p>}
        {error && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
            <FontAwesomeIcon icon={faExclamationCircle} className="h-4 w-4" />
            {error}
          </p>
        )}

        {isLoading && (
          <div className="flex items-center justify-center py-24">
            <FontAwesomeIcon icon={faSpinner} className="h-10 w-10 text-brand-600 animate-spin" />
          </div>
        )}

        {!isLoading && items.length === 0 && (
          <div className="card-surface p-12 text-center">
            <FontAwesomeIcon icon={faInbox} className="h-12 w-12 text-brand-300 mb-4" />
            <h3 className="text-lg font-bold text-brand-950">No {FILTERS[filter].label.toLowerCase()} applications</h3>
          </div>
        )}

        {!isLoading &&
          items.map((a) => {
            const isOpen = openId === a.id;
            const status = STATUS[a.status] ?? STATUS[0];
            return (
              <div key={a.id} className="card-surface overflow-hidden">
                <button
                  onClick={() => setOpenId(isOpen ? null : a.id)}
                  className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 flex-wrap hover:bg-canvas-50"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-brand-950">
                      {a.firstName} {a.lastName}
                    </p>
                    <p className="text-xs text-brand-900/50 truncate">
                      {a.email} · {a.programme}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-brand-900/50">{formatDate(a.submittedAt)}</span>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${status.badge}`}>{status.label}</span>
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-canvas-200 px-5 py-5 space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {row('Email', a.email)}
                      {row('Phone', a.phoneNumber)}
                      {row('ID number', a.idNumber)}
                      {row('Date of birth', a.dateOfBirth ? formatDate(a.dateOfBirth) : null)}
                      {row('Programme', a.programme)}
                      {row('Submitted', formatDateTime(a.submittedAt))}
                    </div>
                    {row('Address', a.address)}
                    {row('Motivation', a.motivation)}

                    {a.status === 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-bold uppercase tracking-wider text-brand-900/40">Documents</p>
                        {DOCUMENT_DISPLAY_ORDER.map((type) => {
                          const doc = a.documents.find((d) => d.documentType === type);
                          return (
                            <div key={type} className="flex items-center justify-between gap-3 border border-canvas-300 rounded-xl px-4 py-2.5">
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-brand-950">{DOCUMENT_TYPE_LABELS[type]}</p>
                                <p className="text-xs text-brand-900/50 truncate">{doc ? doc.fileName : 'Not supplied'}</p>
                              </div>
                              {doc && (
                                <button
                                  onClick={() => view(doc.id)}
                                  className="px-3 py-1.5 border border-canvas-300 text-brand-900 text-xs font-semibold rounded-lg hover:bg-canvas-100 flex items-center gap-1.5"
                                >
                                  <FontAwesomeIcon icon={faEye} className="h-3 w-3" />
                                  View
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {a.status === 0 && (
                      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                        <button
                          onClick={() => accept(a)}
                          disabled={busyId === a.id}
                          className="px-5 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 text-sm flex items-center justify-center gap-2"
                        >
                          <FontAwesomeIcon icon={busyId === a.id ? faSpinner : faCheck} className={`h-3.5 w-3.5 ${busyId === a.id ? 'animate-spin' : ''}`} />
                          Accept
                        </button>
                        <input
                          type="text"
                          value={rejectNotes[a.id] ?? ''}
                          onChange={(e) => setRejectNotes((p) => ({ ...p, [a.id]: e.target.value }))}
                          placeholder="Reason for rejecting (required)"
                          className="flex-1 px-3 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                        />
                        <button
                          onClick={() => reject(a)}
                          disabled={busyId === a.id}
                          className="px-5 py-2 border border-red-200 text-red-600 font-semibold rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 text-sm flex items-center justify-center gap-2"
                        >
                          <FontAwesomeIcon icon={faXmark} className="h-3.5 w-3.5" />
                          Reject
                        </button>
                      </div>
                    )}

                    {a.status === 1 && (
                      <p className="text-sm text-green-700">
                        Accepted {a.reviewedAt ? formatDateTime(a.reviewedAt) : ''}. The documents are on the student record (Students → Edit → Documents).
                      </p>
                    )}
                    {a.status === 2 && (
                      <p className="text-sm text-red-700">
                        Rejected {a.reviewedAt ? formatDateTime(a.reviewedAt) : ''}: {a.reviewNote}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </>
  );
}
